import React from 'react';
import { ExternalLink, Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState(null, '', `#${id}`);
  }
};

const Navbar: React.FC = () => {
  const { lang, setLang } = useI18n();
  const c = COPY[lang];
  const location = useLocation();
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'how-it-works', label: c.nav.howItWorks },
    { id: 'modules', label: c.nav.modules },
    { id: 'use-cases', label: c.nav.useCases },
    { id: 'security', label: c.nav.security },
    { id: 'faq', label: c.nav.faq },
  ];

  const onNavClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      return;
    }
    scrollToId(id);
  };

  const onRequestDemo = () => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/#demo');
      return;
    }
    scrollToId('demo');
    window.setTimeout(() => {
      const first = document.querySelector<HTMLInputElement>('#demo input[name="fullName"]');
      first?.focus();
    }, 200);
  };

  const dashboardHref = 'https://erica.rekayasa.io';

  return (
    <nav
      className={`fixed left-0 right-0 top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur ${
        isScrolled ? 'shadow-sm' : ''
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <a href="#top" onClick={onNavClick('top')} className="flex items-baseline gap-2">
            <span className="text-base font-semibold tracking-tight text-slate-900">
              ERICA
            </span>
            <span className="text-xs text-slate-500">
              {lang === 'id' ? 'oleh Rekayasa' : 'by Rekayasa'}
            </span>
          </a>

          <div className="hidden items-center gap-6 md:flex">
            <div className="flex items-center gap-5">
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={onNavClick(link.id)}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/company"
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
                onClick={() => setIsMenuOpen(false)}
              >
                {c.nav.company}
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={dashboardHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
              >
                {c.nav.dashboard}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>

              <div
                className="inline-flex rounded-xl border border-slate-200 bg-white p-1"
                aria-label={c.nav.languageLabel}
              >
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  className={`h-9 rounded-lg px-3 text-xs font-semibold ${
                    lang === 'en' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setLang('id')}
                  className={`h-9 rounded-lg px-3 text-xs font-semibold ${
                    lang === 'id' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  ID
                </button>
              </div>

              <button
                type="button"
                onClick={onRequestDemo}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
              >
                {c.nav.requestDemo}
              </button>
            </div>
          </div>

          <button
            type="button"
            aria-label="Menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-900 shadow-sm hover:bg-slate-50 md:hidden"
            onClick={() => setIsMenuOpen((v) => !v)}
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {isMenuOpen ? (
        <div className="border-t border-slate-200 bg-white md:hidden">
          <div className="mx-auto max-w-7xl px-4 py-4">
            <div className="grid gap-1">
              <a
                href={dashboardHref}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <span>{c.nav.dashboard}</span>
                <ExternalLink className="h-4 w-4 text-slate-500" />
              </a>
              {navLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={onNavClick(link.id)}
                  className="rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/company"
                onClick={() => setIsMenuOpen(false)}
                className="rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {c.nav.company}
              </Link>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <div
                className="inline-flex flex-1 rounded-xl border border-slate-200 bg-white p-1"
                aria-label={c.nav.languageLabel}
              >
                <button
                  type="button"
                  onClick={() => setLang('en')}
                  className={`h-10 flex-1 rounded-lg text-xs font-semibold ${
                    lang === 'en' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => setLang('id')}
                  className={`h-10 flex-1 rounded-lg text-xs font-semibold ${
                    lang === 'id' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  ID
                </button>
              </div>

              <button
                type="button"
                onClick={onRequestDemo}
                className="inline-flex h-11 flex-1 items-center justify-center rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:from-cyan-500 hover:to-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
              >
                {c.nav.requestDemo}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </nav>
  );
};

export default Navbar;
